// @ts-nocheck
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { getDataView } from "@insite/client-framework/Store/Data/DataState";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByNodeId } from "@insite/client-framework/Store/Links/LinksSelectors";
import PageProps from "@insite/client-framework/Types/PageProps";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { LoadingOverlayProps } from "@insite/mobius/LoadingOverlay";
import Typography from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import DataViewLoadingOverlay from "@store/Library/Components/Toolkit/DataViewLoadingOverlay";
import constants from "@store/constants";
import hasSameParams from "@store/Framework/Common/Utilities/Blog/hasSameParams";
import { getBlogParameters } from "@store/Framework/Store/Store/Pages/BlogList/BlogListSelector";
import getBlogList, { GetBlogApiParameter } from "@store/Framework/Store/Store/Pages/BlogList/Handlers/GetBlogList";
import BlogListTile from "@store/Library/Components/Blog/BlogListTile";
import useMergeProps from "@toolkit/Framework/Common/Hooks/useMergeProps";
import usePersistentDataView from "@toolkit/Framework/Common/Hooks/usePersistentDataView";
import breakpointUtility from "@toolkit/Framework/Common/Utilities/breakpoint";
import breakpointValues from "@toolkit/Framework/Common/Utilities/breakpointValues";
import { pxToRem, pxToRemArray } from "@toolkit/Framework/Common/Utilities/unitHelper";
import { useWindowContext } from "@toolkit/Framework/Components/WindowContext";
import React, { FC, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { css } from "styled-components";

const enum fields {
    noResultsMessage = "noResultsMessage",
    desktopTilesWide = "desktopTilesWide",
    desktopTilesPadding = "desktopTilesPadding",
    mobileVerticalSpacing = "mobileVerticalSpacing",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.noResultsMessage]: string;
        [fields.desktopTilesWide]: number;
        [fields.desktopTilesPadding]: number;
        [fields.mobileVerticalSpacing]: number;
    };
}

type Props = OwnProps;

export interface BlogListViewStyles {
    loadingOverlay?: LoadingOverlayProps;
    flexWrapper?: InjectableCss;
    flexItem?: InjectableCss;
}

const blogListViewStyles: BlogListViewStyles = {
    loadingOverlay: {
        css: css`
            overflow: hidden;
        `,
    },
    flexWrapper: {
        css: css`
            display: flex;
            flex-wrap: wrap;
            ${breakpointValues(pxToRemArray([30, null, 40, 60, null]), "margin-top")};
        `,
    },
    flexItem: {
        css: css`
            position: relative;
            box-sizing: border-box;

            ${breakpointUtility("sm", "down")} {
                &:first-child {
                    margin-top: 0;
                }
            }
        `,
    },
};

const BlogListView: FC<Props> = ({ fields }) => {
    const parameter = useAppSelector(state => getBlogParameters(state));
    const dispatch = useDispatch();
    const { currentBreakpoint } = useWindowContext();
    const [apiParameter, setApiParameter] = useState<GetBlogApiParameter>(parameter);
    const baseBlogListDataView = useAppSelector(state => getDataView(state.data.pages, apiParameter));
    const blogListDataView = usePersistentDataView(baseBlogListDataView);
    const { isLoading, value: blogList } = blogListDataView;
    const currentPage = useAppSelector(state => getPageLinkByNodeId(state, getCurrentPage(state).nodeId));

    useEffect(() => {
        if (!hasSameParams(apiParameter, parameter)) {
            setApiParameter(parameter);
        }
    }, [parameter]);

    useEffect(() => {
        if (!isLoading || !blogList) {
            dispatch(getBlogList(apiParameter));
        }
    }, [apiParameter]);

    const { desktopTilesWide, desktopTilesPadding, mobileVerticalSpacing, noResultsMessage } = fields;
    const tilesWide = desktopTilesWide > 2 && currentBreakpoint === "md" ? 2 : desktopTilesWide;

    const blogListViewDynamicStyles: BlogListViewStyles = {
        flexWrapper: {
            css: css`
                ${breakpointUtility("md")} {
                    margin-left: ${pxToRem(-desktopTilesPadding)};
                    margin-right: ${pxToRem(-desktopTilesPadding)};
                }
            `,
        },
        flexItem: {
            css: css`
                ${breakpointValues(
                    ["100%", null, tilesWide === 1 ? "100%" : "50%", `${100 / tilesWide}%`, null],
                    "width",
                )};

                ${breakpointUtility("md")} {
                    margin: ${pxToRem(desktopTilesPadding, 0)};
                    padding: 0 ${pxToRem(desktopTilesPadding)};

                    &:nth-child(-n + ${tilesWide}) {
                        margin-top: 0;
                    }

                    &:not(:nth-child(${tilesWide}n + 1)) {
                        border-left: 1px solid ${getColor("common.border")};
                    }

                    &:not(:nth-child(-n + ${tilesWide})) {
                        box-shadow: 0 ${`-${desktopTilesPadding - 2}px`} 0 2px white,
                            0 ${`-${desktopTilesPadding + 1}px`} 0 0 ${getColor("common.border")};
                    }
                }

                ${breakpointUtility("sm", "down")} {
                    margin: ${pxToRem(mobileVerticalSpacing, 0)};

                    &:not(:first-child) {
                        box-shadow: 0 ${`-${mobileVerticalSpacing - 2}px`} 0 2px white,
                            0 ${`-${mobileVerticalSpacing + 1}px`} 0 0 ${getColor("common.border")};
                    }
                }
            `,
        },
    };

    const styles = useMergeProps(blogListViewStyles, blogListViewDynamicStyles);

    return (
        <DataViewLoadingOverlay dataView={blogListDataView} {...styles.loadingOverlay}>
            {blogList?.length ? (
                <StyledWrapper {...styles.flexWrapper}>
                    {blogList.map((page: PageProps) => {
                        const {
                            id,
                            fields: {
                                title,
                                publicationDate,
                                hideHourMinutes,
                                previewImage,
                                categoriesList,
                                blogSummary,
                                urlSegment,
                                isArchived,
                            },
                        } = page;

                        return (
                            <StyledWrapper key={id} {...styles.flexItem}>
                                <BlogListTile
                                    title={title}
                                    publicationDate={publicationDate}
                                    hideHourMinutes={hideHourMinutes}
                                    previewImage={previewImage}
                                    categoriesList={categoriesList}
                                    blogSummary={blogSummary}
                                    blogLink={`${currentPage?.url}/${urlSegment}`}
                                    isArchived={isArchived}
                                ></BlogListTile>
                            </StyledWrapper>
                        );
                    })}
                </StyledWrapper>
            ) : (
                <Typography>{noResultsMessage}</Typography>
            )}
        </DataViewLoadingOverlay>
    );
};

const widgetModule: WidgetModule = {
    component: BlogListView,
    definition: {
        group: constants.widgetGroup.blogList,
        displayName: "Blog List View",
        allowedContexts: ["BlogListPage"] as any,
        fieldDefinitions: [
            {
                name: fields.noResultsMessage,
                displayName: "No Results Message",
                defaultValue: "There are no results available or matching the current search criteria.",
                editorTemplate: "MultilineTextField",
                fieldType: "Translatable",
                isRequired: true,
            },
            {
                name: fields.desktopTilesWide,
                displayName: "How many tiles wide in desktop view",
                editorTemplate: "IntegerField",
                defaultValue: 3,
                fieldType: "General",
                min: 1,
                max: 4,
                isRequired: true,
            },
            {
                name: fields.desktopTilesPadding,
                displayName: "Desktop Tiles Padding",
                editorTemplate: "IntegerField",
                defaultValue: 20,
                fieldType: "General",
                min: 0,
                max: 60,
                isRequired: true,
            },
            {
                name: fields.mobileVerticalSpacing,
                displayName: "Mobile Tiles Vertical Spacing",
                editorTemplate: "IntegerField",
                defaultValue: 20,
                fieldType: "General",
                min: 0,
                max: 60,
                isRequired: true,
            },
        ],
    },
};

export default widgetModule;
